import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Alert } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import AddIcon from '@mui/icons-material/Add';
import RadarIcon from '@mui/icons-material/Radar';
import DescriptionIcon from '@mui/icons-material/Description';
import { COLORS } from '../../../constants/radar';
import Modal from '../../common/Modal';
import SkillLevelSelector from '../../TrendRadarChart/components/TreeChart/components/SkillLevelSelector';
import TechnologyHierarchyPath from './TechnologyHierarchyPath';

const OPERATION_TYPES = {
  ADD_TO_RADAR: 'addToRadar',
  ADD_TO_CV: 'addToCV',
  SYNC_TO_CV: 'syncToCV',
  SYNC_TO_RADAR: 'syncToRadar',
};

const OPERATION_CONFIG = {
  [OPERATION_TYPES.ADD_TO_RADAR]: {
    title: 'Add to Tech Radar',
    icon: RadarIcon,
    color: COLORS.PRIMARY,
    confirmText: 'Add to Radar',
    getMessage: (techName) => `Are you sure you want to add "${techName}" to the Tech Radar?`,
  },
  [OPERATION_TYPES.ADD_TO_CV]: {
    title: 'Add to CV',
    icon: DescriptionIcon,
    color: '#2563eb',
    confirmText: 'Add to CV',
    getMessage: (techName) => `Are you sure you want to add "${techName}" to the CV?`,
  },
  [OPERATION_TYPES.SYNC_TO_CV]: {
    title: 'Sync to CV',
    icon: SyncIcon,
    color: '#d97706',
    confirmText: 'Sync to CV',
    getMessage: (techName) => `Are you sure you want to sync "${techName}" level from Tech Radar to CV?`,
  },
  [OPERATION_TYPES.SYNC_TO_RADAR]: {
    title: 'Sync to Tech Radar',
    icon: SyncIcon,
    color: '#d97706',
    confirmText: 'Sync to Radar',
    getMessage: (techName) => `Are you sure you want to sync "${techName}" level from CV to Tech Radar?`,
  },
};

const TechnologySyncDialog = ({
  open,
  onClose,
  onConfirm,
  operationType,
  technology,
  skillLevels = [],
  loading = false,
  error = null,
}) => {
  const [selectedLevelId, setSelectedLevelId] = useState(null);
  const config = OPERATION_CONFIG[operationType];

  // Determine if we need level selection
  // Only require level selection when level is explicitly -1 (meaning "not set but required")
  // Null levels don't need selection as they represent "no level needed"
  const needsLevelSelection = (() => {
    if (!technology) return false;

    switch (operationType) {
      case OPERATION_TYPES.ADD_TO_RADAR:
        // Adding from CV to Radar - check CV level (only -1 needs selection)
        return technology.cvLevel?.level === -1;
      case OPERATION_TYPES.ADD_TO_CV:
        // Adding from Radar to CV - check Radar level (only -1 needs selection)
        return technology.radarLevel?.level === -1;
      case OPERATION_TYPES.SYNC_TO_CV:
        // Syncing Radar level to CV - check Radar level
        return technology.radarLevel?.level === -1;
      case OPERATION_TYPES.SYNC_TO_RADAR:
        // Syncing CV level to Radar - check CV level
        return technology.cvLevel?.level === -1;
      default:
        return false;
    }
  })();

  // Reset selected level when dialog opens with different technology
  useEffect(() => {
    if (open) {
      if (needsLevelSelection) {
        setSelectedLevelId(null);
      } else {
        // Pre-select the source level
        switch (operationType) {
          case OPERATION_TYPES.ADD_TO_RADAR:
          case OPERATION_TYPES.SYNC_TO_RADAR:
            setSelectedLevelId(technology?.cvLevel?.id);
            break;
          case OPERATION_TYPES.ADD_TO_CV:
          case OPERATION_TYPES.SYNC_TO_CV:
            setSelectedLevelId(technology?.radarLevel?.id);
            break;
          default:
            setSelectedLevelId(null);
        }
      }
    }
  }, [open, technology, operationType, needsLevelSelection]);

  if (!config || !technology) return null;

  const IconComponent = config.icon;

  const handleSubmit = (e) => {
    e.preventDefault();
    const levelId = needsLevelSelection ? selectedLevelId : getDefaultLevelId();
    onConfirm(technology, levelId);
  };

  const getDefaultLevelId = () => {
    switch (operationType) {
      case OPERATION_TYPES.ADD_TO_RADAR:
      case OPERATION_TYPES.SYNC_TO_RADAR:
        return technology.cvLevel?.id;
      case OPERATION_TYPES.ADD_TO_CV:
      case OPERATION_TYPES.SYNC_TO_CV:
        return technology.radarLevel?.id;
      default:
        return null;
    }
  };

  const canConfirm = !loading && (!needsLevelSelection || selectedLevelId);

  const getSubmitText = () => {
    if (loading) return 'Processing...';
    return config.confirmText;
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={config.title}
      submitText={getSubmitText()}
      cancelText="Cancel"
      submitDisabled={!canConfirm}
      maxWidth="500px"
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <p style={{ margin: '0 0 16px 0', color: COLORS.GRAY }}>
          {config.getMessage(technology.name)}
        </p>

        <Box sx={{
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          p: 2,
          border: '1px solid #e2e8f0',
        }}>
          <Box sx={{
            fontWeight: 600,
            color: COLORS.PRIMARY,
            mb: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}>
            <IconComponent sx={{ fontSize: 18, color: config.color }} />
            {technology.name}
          </Box>
          <TechnologyHierarchyPath hierarchy={technology.hierarchy} hideFirstLevel hideLastLevel />
        </Box>
      </Box>

      {needsLevelSelection && (
        <Box sx={{ mt: 2 }}>
          <p style={{ margin: '0 0 12px 0', color: COLORS.GRAY, fontSize: '14px' }}>
            Select a competency level:
          </p>
          <SkillLevelSelector
            selectedLevelId={selectedLevelId}
            onLevelSelect={setSelectedLevelId}
            skillName={technology.name}
            showHeader={false}
            skillLevels={skillLevels}
          />
        </Box>
      )}
    </Modal>
  );
};

TechnologySyncDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  operationType: PropTypes.oneOf(Object.values(OPERATION_TYPES)),
  technology: PropTypes.shape({
    leafSegmentId: PropTypes.string,
    name: PropTypes.string,
    hierarchy: PropTypes.array,
    radarLevel: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    cvLevel: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  }),
  skillLevels: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

export { OPERATION_TYPES };
export default TechnologySyncDialog;
